import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// axios defaults: include cookies for auth
axios.defaults.withCredentials = true;

// One-time interceptor: if any request returns 401, try /auth/refresh once
// (uses http-only refresh cookie) and retry the original request. Prevents
// the admin from being logged out after the access token expires.
let _isRefreshing = false;
let _refreshWaiters = [];
const _flushWaiters = (ok) => {
  _refreshWaiters.forEach((cb) => cb(ok));
  _refreshWaiters = [];
};
if (!axios.__smAuthInterceptor) {
  axios.__smAuthInterceptor = true;
  axios.interceptors.response.use(
    (r) => r,
    async (error) => {
      const original = error.config || {};
      const url = original.url || "";
      // Never try to refresh the refresh endpoint itself or the login flow.
      if (
        error.response?.status !== 401 ||
        original._retried ||
        /\/auth\/(login|refresh|logout|magic-link|verify-2fa)/.test(url)
      ) {
        return Promise.reject(error);
      }
      original._retried = true;
      if (_isRefreshing) {
        return new Promise((resolve, reject) => {
          _refreshWaiters.push((ok) =>
            ok ? resolve(axios(original)) : reject(error),
          );
        });
      }
      _isRefreshing = true;
      try {
        await axios.post(`${API}/auth/refresh`);
        _isRefreshing = false;
        _flushWaiters(true);
        return axios(original);
      } catch (e) {
        _isRefreshing = false;
        _flushWaiters(false);
        return Promise.reject(error);
      }
    },
  );
}

export const AuthContext = createContext(null);

export function formatApiError(detail) {
  if (detail == null) return "Niečo sa pokazilo. Skúste znovu.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  // null = checking, false = unauthenticated, object = authed user
  const [user, setUser] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    if (data && data.pending_2fa) {
      return data; // caller shows 2FA UI
    }
    setUser(data);
    return data;
  }, []);

  const requestMagicLink = useCallback(async (email) => {
    const { data } = await axios.post(`${API}/auth/magic-link/request`, { email });
    return data;
  }, []);

  const verifyMagicToken = useCallback(async (token) => {
    const { data } = await axios.post(`${API}/auth/magic-link/verify`, { token });
    setUser(data);
    return data;
  }, []);

  const verify2FA = useCallback(async (email, code, trustDevice = false) => {
    const { data } = await axios.post(`${API}/auth/verify-2fa`, {
      email,
      code,
      trust_device: !!trustDevice,
    });
    setUser(data);
    return data;
  }, []);

  const resend2FA = useCallback(async (email) => {
    await axios.post(`${API}/auth/resend-2fa`, { email });
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {
      /* ignore */
    }
    setUser(false);
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, verify2FA, resend2FA, requestMagicLink, verifyMagicToken, logout, refresh, API }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
